/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  LearnerProfile_Ethnicity as Ethnicity,
  LearnerProfile_GenderEnum as GenderOptions,
  LearnerProfile_OccupationPreferenceLevel as LearnerProfileOccupationPreferenceLevel,
  LearnerProfile_VeteranStatus as VeteranStatus,
} from '__generated__/graphql-types';
import type {
  LearnerProfile_GenderEnum as GenderType,
  LearnerProfile_OccupationPreference as LearnerProfileOccupationPreference,
  LearnerProfile_SkillAssessmentProficiency as SkillProficiency,
} from '__generated__/graphql-types';

import { Grid, Typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { LearnerProfile } from 'bundles/account-profile/common/types';
import type { OccupationIdToRoleMap } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { getProfileUrl } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileSlugUtils';

import _t from 'i18n!nls/account-profile';

export const getColorByProficiency = (theme: Theme, proficiency?: string) => {
  switch (proficiency) {
    case 'PROFICIENT':
      return {
        tagColor: theme.palette.green[100],
        dotColor: theme.palette.green[700],
      };
    case 'EXPERIENCED':
      return {
        tagColor: theme.palette.blue[50],
        dotColor: theme.palette.blue[600],
      };
    case 'INTERMEDIATE':
      return {
        tagColor: theme.palette.yellow[100],
        dotColor: theme.palette.yellow[500],
      };
    case 'BEGINNER':
      return {
        tagColor: theme.palette.red[50],
        dotColor: theme.palette.red[700],
      };
    default:
      return null;
  }
};

const styles = {
  tagWrapper: css`
    display: flex;
    align-items: center;
  `,
  tag: (theme: Theme) => css`
    display: flex;
    align-items: center;
    border-radius: 4px;
    gap: ${theme.spacing(4)};
    padding: ${theme.spacing(0, 4)};
    height: 22px;
  `,
  proficiencyTag: (theme: Theme, proficiency?: string) => {
    const proficiencyColor = getColorByProficiency(theme, proficiency);
    return css`
      background-color: ${proficiencyColor?.tagColor};

      .dot {
        width: 8px;
        height: 8px;
        border-radius: 4px;
        background-color: ${proficiencyColor?.dotColor};
      }
    `;
  },
  capitalizeText: css`
    text-transform: capitalize;
  `,
};

export const convertLevelEnumToString = (level?: LearnerProfileOccupationPreferenceLevel | null) => {
  switch (level) {
    case LearnerProfileOccupationPreferenceLevel.JuniorIndividualContributor:
      return _t('Entry-level');
    case LearnerProfileOccupationPreferenceLevel.ExecutiveManagement:
      return _t('Executive');
    case LearnerProfileOccupationPreferenceLevel.Intern:
      return _t('Intern');
    case LearnerProfileOccupationPreferenceLevel.ManagerialLeadershipRole:
      return _t('Lead / Manager');
    case LearnerProfileOccupationPreferenceLevel.SeniorIndividualContributor:
      // This was incorrectly mapped and we need to continue to keep the relationship
      return _t('Mid-level');
    case LearnerProfileOccupationPreferenceLevel.Director:
      return _t('Senior manager / Director');
    case LearnerProfileOccupationPreferenceLevel.NotApplicable:
    default:
      return '';
  }
};

export const getProficiencyIdToLabel = (): Record<string, string> => ({
  BEGINNER: _t('Beginner'),
  INTERMEDIATE: _t('Intermediate'),
  EXPERIENCED: _t('Experienced'),
  PROFICIENT: _t('Proficient'),
});

export const getRolesAndPreferences = (
  occupationPreferences: LearnerProfileOccupationPreference[] | undefined,
  idToRolesMap: OccupationIdToRoleMap
): { [roleName: string]: Array<string> } =>
  occupationPreferences?.reduce((acc, pref) => {
    const preferenceName =
      'id' in pref.occupation ? idToRolesMap.get(pref.occupation.id)?.occupationName ?? '' : pref.occupation.name;

    const convertedLevel = convertLevelEnumToString(pref.level);
    return {
      ...acc,
      [preferenceName]: [...(acc[preferenceName] ?? []), ...(convertedLevel ? [convertedLevel] : [])],
    };
  }, {} as { [roleName: string]: Array<string> }) || {};

export type ValidGenderOptionTypes = GenderType | 'GENDER_NOT_LISTED';

export const convertGenderEnumToString = (gender?: ValidGenderOptionTypes) => {
  switch (gender) {
    case GenderOptions.Agender:
      return _t('Agender');
    case GenderOptions.Woman:
      return _t('Woman');
    case GenderOptions.Man:
      return _t('Man');
    case GenderOptions.PreferNotToSay:
      return _t('Prefer not to say');
    case GenderOptions.NonBinary:
      return _t('Non binary');
    case GenderOptions.TransgenderMan:
      return _t('Transgender Man');
    case GenderOptions.TransgenderWoman:
      return _t('Transgender Woman');
    case 'GENDER_NOT_LISTED':
      return _t('Gender identity not listed');
    default:
      return '';
  }
};

export const convertEthnicityEnumToString = (ethnicity?: Ethnicity) => {
  switch (ethnicity) {
    case Ethnicity.Hispanic:
      return _t('Hispanic or Latino');
    case Ethnicity.White:
      return _t('White');
    case Ethnicity.AfricanAmerican:
      return _t('Black or African American');
    case Ethnicity.NativeHawaiian:
      return _t('Native Hawaiian or Other Pacific Islander');
    case Ethnicity.Asian:
      return _t('Asian');
    case Ethnicity.AmericanIndian:
      return _t('American Indian or Alaska Native');
    case Ethnicity.PreferNotToSay:
      return _t('Prefer not to say');
    default:
      return '';
  }
};

export const convertVeteranStatusEnumToString = (veteranStatus?: VeteranStatus) => {
  switch (veteranStatus) {
    case VeteranStatus.ProtectedVeteran:
      return _t('I identify as one or more of the classifications of a protected veteran');
    case VeteranStatus.NotProtectedVeteran:
      return _t('I am not a protected veteran');
    case VeteranStatus.PreferNotToSay:
      return _t('Prefer not to say');
    default:
      return '';
  }
};

// nit: have to hardcode order of gender options, need to modify it if gender enum is changed
export const GENDER_OPTIONS: ValidGenderOptionTypes[] = [
  GenderOptions.Woman,
  GenderOptions.Man,
  GenderOptions.NonBinary,
  GenderOptions.TransgenderWoman,
  GenderOptions.TransgenderMan,
  GenderOptions.Agender,
  'GENDER_NOT_LISTED',
  GenderOptions.PreferNotToSay,
];

export const REDUCED_GENDER_OPTIONS: ValidGenderOptionTypes[] = [
  GenderOptions.Woman,
  GenderOptions.Man,
  GenderOptions.PreferNotToSay,
];

export const ETHNICITY_OPTIONS = [
  Ethnicity.Hispanic,
  Ethnicity.White,
  Ethnicity.AfricanAmerican,
  Ethnicity.NativeHawaiian,
  Ethnicity.Asian,
  Ethnicity.AmericanIndian,
  Ethnicity.PreferNotToSay,
] as const;

export const VETERAN_STATUS_OPTIONS = [
  VeteranStatus.ProtectedVeteran,
  VeteranStatus.NotProtectedVeteran,
  VeteranStatus.PreferNotToSay,
] as const;

export const handleShareProfile = (
  showToPublic: boolean,
  profile: Partial<LearnerProfile>,
  onOpen?: () => void,
  onFailureCallback?: () => void
) => {
  if (showToPublic) {
    navigator.clipboard
      .writeText(getProfileUrl(profile))
      .then(() => onOpen && onOpen())
      .catch(() => onFailureCallback && onFailureCallback());
  } else if (onOpen) onOpen();
};

export const SkillProficiencyTag: React.VFC<{
  proficiency?: SkillProficiency;
  boldedContent: React.ReactNode;
  regularContent: React.ReactNode;
  customCss?: Interpolation<Theme>;
}> = ({ proficiency, boldedContent, regularContent, customCss }) => {
  const theme = useTheme();
  return (
    <Grid item css={[styles.tagWrapper, customCss]}>
      <div css={[styles.tag, styles.proficiencyTag(theme, proficiency)]}>
        <div className="dot" />
        <Typography2 variant="subtitleMedium" component="h3" css={styles.capitalizeText}>
          {boldedContent}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" css={styles.capitalizeText}>
          {regularContent}
        </Typography2>
      </div>
    </Grid>
  );
};
